import BusinessMetricFeature from "@components/business-metric-feature";
import {
  SHButton,
  SHImage,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import SHSkeleton from "@components/design-systems/sh-skeleton";
import { ArrowDownSVG, EditSVG } from "@components/svgs";
import DollarSVG from "@components/svgs/dollar";
import FireSVG from "@components/svgs/fire";
import NetFlowsSVG from "@components/svgs/net-flows";
import UsersArSVG from "@components/svgs/users-ar";
import { useInvestmentProduct } from "@hooks/useInvestmentProduct";
import { useUserPermissions } from "@hooks/userUserPermission";
import { useTheme } from "@mui/material";
import { RootState } from "@redux/store";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useEditInvestmentProduct } from "@hooks/useEditInvestmentProduct";
import {
  updateInvestmentProductName,
  updateProductType,
} from "@redux/slices/product/investment-product";
import { ProductType } from "@models/product/enums/productType";

interface InvestmentBannerProps {
  isOtherSupplierUser?: boolean;
}

export const Banner = ({
  isOtherSupplierUser = false,
}: InvestmentBannerProps) => {
  const { palette } = useTheme();
  const {
    investmentProductBrandingData,
    investmentProductUI: { isLoadingEditMode, isLoading },
  } = useSelector((state: RootState) => state.investmentProduct);
  const { investmentProductId } = useParams();
  const { checkEditInvestmentProduct } = useEditInvestmentProduct();
  const { canGoToEditProduct } = useUserPermissions();
  const { loadInvestmentProductBrandingInfo } = useInvestmentProduct();
  const dispatch = useDispatch();

  const handleOnEditInvestmentProduct = () => {
    checkEditInvestmentProduct(investmentProductId || "");
    dispatch(updateInvestmentProductName(investmentProductBrandingData?.name));
  };

  useEffect(() => {
    loadInvestmentProductBrandingInfo(investmentProductId ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentProductId]);

  if (!investmentProductBrandingData || isLoading) {
    return (
      <SHStack
        height={400}
        width="100%"
        sx={{
          padding: "24px 30px 30px",
          justifyContent: "flex-end",
        }}
      >
        <SHStack spacing={2}>
          <SHSkeleton height={30} width={"60%"} />
          <SHStack direction="row" spacing={2}>
            <SHSkeleton height={100} width={100} />
            <SHStack spacing={2} width={"100%"}>
              <SHSkeleton height={30} width={"30%"} />
              <SHSkeleton height={52} width={"100%"} />
            </SHStack>
          </SHStack>
        </SHStack>
      </SHStack>
    );
  }

  const {
    banner,
    logo,
    name,
    website,
    firstSmaLaunched,
    smaFum,
    noOfModel,
    availableOn,
    investmentType,
  } = investmentProductBrandingData;

  dispatch(updateProductType(investmentType as ProductType));

  return (
    <SHStack
      direction={"column"}
      spacing={2}
      sx={{
        backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.15) 31.58%, rgba(0, 0, 0, 0.282) 47.04%, rgba(0, 0, 0, 0.42) 63.99%, rgba(0, 0, 0, 0.6) 100%), url("${banner}")`,
        backgroundColor: palette.common.white,
        height: "400px",
        width: "100%",
        padding: "24px 30px 30px",
        gap: "10px",
        justifyContent: "flex-end",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <SHStack direction="row" spacing="32px" alignItems="flex-end">
        <SHTypography
          variant="h1"
          colorVariant="white"
          sx={{
            textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
        >
          {name}
        </SHTypography>
        <SHTypography
          variant="subtitle1"
          colorVariant="white"
          sx={{
            fontSize: "15px",
            paddingBottom: "9px",
            columnGap: "10px",
          }}
        >
          <span style={{ paddingRight: "9px" }}>{website}</span>
          <ArrowDownSVG
            color={palette.common.white}
            style={{
              paddingLeft: "5px",
              translate: "0px 5px ",
              transform: "rotate(270deg)",
            }}
          />
        </SHTypography>
      </SHStack>
      <SHStack direction="row">
        <SHStack sx={{ flexDirection: "column-reverse" }}>
          <SHImage
            height={107}
            width={107}
            src={logo}
            style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)" }}
          />
        </SHStack>

        <SHStack
          justifyContent={"space-between"}
          alignItems="flex-start"
          marginLeft={"30px"}
          display={"flex"}
          spacing={"25px"}
          sx={(theme) => ({
            [theme.breakpoints.down(1050)]: {
              width: "100%",
            },
          })}
        >
          <SHButton
            variant="contained"
            size="extraMedium"
            sx={{
              fontWeight: 700,
              visibility:
                !isOtherSupplierUser && canGoToEditProduct(investmentProductId)
                  ? "initial"
                  : "hidden",
            }}
            disabled={isLoadingEditMode}
            isLoading={isLoadingEditMode}
            startIcon={<EditSVG color={palette.common.white} />}
            onClick={handleOnEditInvestmentProduct}
          >
            {`Edit ${name || ""}`}
          </SHButton>
          <SHStack
            gap="40px"
            direction="row"
            flexWrap={"wrap"}
            sx={(theme) => ({
              [theme.breakpoints.down(1050)]: {
                gap: "6px",
                display: "grid",
                gridTemplateColumns: "2fr 4fr",
                width: "100%",
              },
            })}
          >
            <BusinessMetricFeature
              title="First SMA launched"
              value={firstSmaLaunched}
              icon={<FireSVG />}
            />
            <BusinessMetricFeature
              title="SMA FUM"
              value={smaFum}
              icon={<DollarSVG />}
            />
            <BusinessMetricFeature
              title="No. of models"
              value={noOfModel}
              icon={<NetFlowsSVG />}
            />
            <BusinessMetricFeature
              title="Available on"
              value={availableOn}
              icon={<UsersArSVG />}
            />
          </SHStack>
        </SHStack>
      </SHStack>
    </SHStack>
  );
};
